// Where the wire meets the WebGL2 client: one `Handlers` object, one arm a
// frame kind, and the little bit of world state the arms keep between them
// (plan C.2 `fast/feed.ts`).
//
// Every arm either hands the frame to `state.ts`, which is what `main.ts`
// does with it, or feeds one map layer. `onHello` builds the world,
// `onState` moves it, `onEvents` sets off the blasts, `onHeatmap` fills the
// field, and the two site arms keep the site layer and the state store in
// step.
//
// **Nothing is dropped on the floor.** A state frame is a DELTA (protocol
// v4): a client that ignores one has not skipped a picture, it has lost the
// change for good. One place per handler arm is the rule; keep it.
//
// **The arrival is an arm of the hello** (R163, R182, R499). `onHello` names
// which one this is BEFORE it stores the hello, because the answer is a
// difference between the hello that was and the one that came; it covers the
// screen before the store and starts the arrival at the end. Everything the
// arrival then plays — the map's own animation, the chrome dock-in, the
// season banner and the chart's travel — hangs off the `arrival` dep, which
// `fast/main.ts` fills. A client that hands over none plays none, and the
// hello arm is otherwise the same.
//
// The two hex views take their world from here as well (R456, R501): the
// hello hands over the owners array the scene applied, and every flip is one
// `invalidate` a view. A hidden view pays nothing for that flag, so a whole
// season of flips on the shipped picture costs two writes a tick.
//
// **The map half is NOT here** (R503, the parity plan of 2026-09-04, Phase
// Phase B). `fast/scene.ts` owns the layers both pages draw, and this file owns
// what the core page alone has: the arrival, the chart, the heat map, the
// two hex views, the clan room, the market, the inbox, the replay, the
// scoreboard, the WAR tab, the developer build, the notices, the chat and
// the re-join a refused ticket asks for. Each map arm below is one call into
// the scene, and three of the scene's calls come back as a hook.

import type { SpawnKind } from '../render/spawn-plan';
import { SquadWatch, changeNotice, pickupNotice, pickupNoticeDue } from '../ui/squad';
import type { ClanInfo, Empire, Hello, InboxMsg, PointOfInterest, Replay, State } from '../gen/proto/aquaphobia';
import { poiTitle } from '../ui/cards/mapcards';
import { poiShown } from '../survey';
import type { NoticeOptions } from '../ui/notices';
import { ChatChannel, EventKind, HeatmapKind } from '../gen/proto/aquaphobia';
import type { Event as WireEvent } from '../gen/proto/aquaphobia';
import { classOfOrder, signatureLine } from '../ui/cards/approach';
import type { Connection, FatalKind, Handlers } from '../net/connection';
import { decodeTimelapse } from '../net/timelapse';
import type { EventBus } from '../events';
import { EV_SQUAD_PING, squadPingLine, type CueWatch } from '../audio/cues';
import type { AudioDirector } from '../audio/director';
import type { UnlockQueue } from '../ui/unlock';
import type { Buzzer } from '../ui/buzz';
import type { TabIcon } from '../ui/tabicon';
import { heatServerKind, type GameState } from '../state';
import type { Overlay } from '../ui/overlay';
import { wearTheme } from '../ui/themestate';
import { ResultCue, tableCues } from '../ui/casino/resultcue';
import { reducedMotion } from '../render/motion';
import { squadSlotsOf } from '../render/fleet-marks';
import { MPX_PER_PX, PROTOCOL, cellAt } from '../world';
import { CHROME_INBOX_REFETCH_MS } from '../gen/presentation';
import { main as S } from '../gen/strings';
import type { MapScene } from './scene';
import type { HeatmapLayer } from './layers/heatmap';
import type { HexBorderLayer, HexFillLayer } from './layers/hexgrid';
import type { Minimap } from './minimap';
import type { Commands } from './commands';
import type { Tooltip } from './tooltip';

/** What the feed is handed. The lazily-read members are built after it. */
export interface FeedDeps {
  /** The map both pages draw (R503). Every map arm below is one call into it. */
  scene: MapScene;
  state: GameState;
  bus: EventBus;
  heatmap: HeatmapLayer;
  /**
   * The two hex-grid views (R456, R501). They HOLD the owners array the
   * territory layer mutates in place, so the hello hands it over once and
   * every flip is one flag.
   */
  hexFills: HexFillLayer;
  hexBorders: HexBorderLayer;
  minimap: Minimap;
  /** The map's decisions. Built after the overlay, so it is read. */
  commands: () => Commands;
  /** The map tooltip. Built after the overlay, so it is read. */
  tooltip: () => Tooltip;
  /** Opens the replay viewer on a served film (v12). */
  openReplay: (r: Replay) => void;
  /** The overlay. It is built after the actions, which are built after this. */
  ui: () => Overlay;
  /** The socket. The hello arm answers on it, so it is read and not held. */
  conn: () => Connection;
  /** Reports the visible rect to the server. Called once per hello. */
  reportViewport: () => void;
  /**
   * The cue watcher (R547), or nothing in a client that plays none. It reads
   * the frames the arms below already take and turns the edges the wire
   * states but never announces into a cue.
   */
  cues?: CueWatch;
  /**
   * The one owner of sound on this page, or nothing in a client that
   * plays none.
   *
   * Almost every cue rides the event bus or the cue watcher. The casino
   * arms are the exception: a seat, a deal, a chip, a turn, a spinning
   * wheel and a settled hand are edges of a frame this file already
   * takes, and none has an event row to hang a toast on.
   */
  audio?: AudioDirector;
  /**
   * The unlock cards (achievements.md §4, R936), or nothing in a client
   * that draws none. The queue owns the card, the hold, the collapse and
   * the line the card leaves in the notices column; this arm only files
   * what the wire said.
   */
  unlocks?: UnlockQueue;
  /**
   * The buzz of a telephone (R1612), or nothing in a client that buzzes
   * none. It hears the notices and the hit events the arms below take.
   */
  buzz?: Buzzer;
  /**
   * The live tab icon (R1653), or nothing on a page with none. A siege
   * warning that reaches a hidden tab puts its dot on the icon.
   */
  tabIcon?: Pick<TabIcon, 'onNotice'>;
  /**
   * A season summary arrived (R1518). The state store already holds it;
   * the page asks the achievement record again here, because the card
   * names the grades of the season that just locked.
   */
  onSeasonSummary?: () => void;
  /** An Empire frame arrived, after the state store took it. The page
   *  reads the advisor switch of the first one here (R2202). */
  onEmpire?: (e: Empire) => void;
  /** The connection is down for good. */
  onFatal: (reason: string, kind: FatalKind) => void;
  /** A refused join ticket, in meta mode only. */
  onTicketRejected?: () => void;
  /** The season rolled and the world handed this client over, in meta
   *  mode only (R766). */
  onSeasonRolled?: () => void;
  /** The world closed this socket because a maintenance window opened
   *  (close code 4004, deployment.md §6.1). */
  onMaintenance?: () => void;
  /** Three plain reconnect failures in a row: a hint that a window may
   *  stand, which the caller answers by probing the gate document. */
  onMaintenanceSuspect?: () => void;
  /**
   * The arrival (R163, R182, R499), or nothing in a client that plays none.
   *
   * `cover` runs before the hello is stored, so a season roll takes the
   * screen black before the world it is replacing goes; the caller covers on
   * `'season'` alone, because a login is covered from boot and a resync is
   * not an arrival at all. `start` runs at the end of the arm.
   */
  arrival?: {
    cover(kind: SpawnKind): void;
    start(kind: SpawnKind): void;
  };
}

/** What changes when an event files or folds a message: the count of
 *  rows, and the id, the stamp and the members of the newest row (a fold
 *  that takes a member moves to the head). A read mark moves none of it. */
function inboxMark(list: readonly InboxMsg[]): string {
  const m = list[0];
  return m === undefined ? '0' : `${list.length}:${m.id}:${Number(m.unix)}:${m.count}`;
}

/**
 * One line in the notices column for each signature order spent in one
 * event batch (R2376, ui-map-interface.md §8 "The approach on the card").
 * The server sends the event only for a hull on the viewer's picture, so
 * the line names nothing the map does not show. The line carries the cell
 * of the hull, so it is a GO TO press (R2370).
 */
function signatureNotices(d: FeedDeps, events: readonly WireEvent[]): void {
  for (const e of events) {
    if (e.kind !== EventKind.EVENT_KIND_SIGNATURE) continue;
    const f = d.scene.fleets.describe(e.fleet);
    const hull = f ? { own: f.own, friend: f.faction === d.state.faction, name: f.name } : null;
    const line = signatureLine(e, hull, (o) => classOfOrder(d.state.catalog?.ships ?? [], o));
    if (!line) continue;
    const cell = cellAt(e.xMpx / MPX_PER_PX, e.yMpx / MPX_PER_PX);
    d.ui().notice(line, { key: `sig:${e.fleet}`, cell: cell > 0 ? cell : 0 });
  }
}

/**
 * The world the socket describes, and the handlers that keep it current.
 *
 * The map is the scene's (R503). This object holds the arms the core page
 * alone has, plus the one figure no layer owns: the tick of the last state
 * frame. What the map holds, a caller reads off the scene it built.
 */
export class Feed {
  /** The tick of the last state frame, for the statistics line. */
  lastTick = 0;

  /** The unread count of the last empire frame, to read the edge (R694). */
  private lastUnread = 0;
  /** When the client last asked for the inbox, on the wall clock. */
  private lastInboxFetchMs = 0;
  /** A rise the floor held back. It fires on a later empire frame. */
  private pendingInboxFetch = false;
  /** Tells a squad change from a repeat clan frame (R1571, card 18). */
  private readonly squadWatch = new SquadWatch();

  readonly handlers: Handlers;

  constructor(private readonly deps: FeedDeps) {
    this.handlers = this.build();
  }

  private build(): Handlers {
    const d = this.deps;
    // The held casino result cues of this feed (R1210).
    const resultCue = new ResultCue({
      play: (id) => d.audio?.play(id),
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    });
    return {
      onTicketRejected: d.onTicketRejected,
      onSeasonRolled: d.onSeasonRolled,
      onMaintenance: d.onMaintenance,
      onMaintenanceSuspect: d.onMaintenanceSuspect,

      onHello: (h) => this.onHello(h),
      onState: (tick, s) => this.onState(tick, s),

      // The empire frame is the control groups' store (R539): `setEmpire`
      // takes the taken slots whole, and the badge layer and the chrome are
      // handed the new picture in the same breath.
      onEmpire: (e) => {
        d.state.setEmpire(e);
        this.pollInbox(e.inboxUnread);
        d.commands().pushGroups();
        // The queue, the prints, the expeditions, the tech and the captains
        // all change here and announce nothing. The watcher reads the edges
        // (R547); the tech table it compares against is the hello's.
        // The grade ladder names the masterwork modules of a new print (R3135).
        d.cues?.onEmpire(e, d.state.catalog?.tech ?? [], d.state.catalog?.rules?.moduleGrades ?? []);
        d.onEmpire?.(e);
      },

      onSiteOwner: (siteId, faction, amount, capacity, effect) => {
        d.cues?.onSiteOwner(siteId, amount);
        d.scene.setSiteOwner(siteId, faction, amount, capacity, effect);
        d.state.setSiteOwner(siteId, faction, amount, capacity, effect);
        d.state.changed();
        d.tooltip().refreshAtCursor();
      },

      onSiteAdded: (site) => {
        d.scene.addSite(site);
        d.state.sites.set(site.id, site);
      },

      // The mirror of `onSiteAdded` (R204). The seaquake's stage 2 kills
      // every deposit, and the site is gone rather than worked out: every
      // layer and every cache that holds it by id must let it go in the same
      // frame, or the map keeps a field nobody can work.
      onSiteRemoved: (siteId) => {
        const gone = d.state.sites.get(siteId) ?? d.scene.site(siteId);
        d.scene.removeSite(siteId, gone);
        d.state.removeSite(siteId);
        // A panel must not sit open on nothing. The selection goes through
        // the one door, so the aim mode and the bracket follow it.
        if (d.state.selection?.kind === 'site' && d.state.selection.id === siteId) {
          d.commands().select(null);
        }
        d.tooltip().refreshAtCursor();
      },

      onEvents: (events) => {
        d.scene.applyEvents(events);
        // A hit on an own fleet buzzes a telephone (R1612).
        d.buzz?.onEvents(events, d.state.playerId);
        signatureNotices(d, events);
      },

      // The chart's imprint prints how many notices this session took
      // (R548), so the count is kept before the bus decides what to show:
      // a notice a row silenced was still a correction to the chart.
      onNotice: (n) => {
        d.state.countNotice();
        d.bus.server(n);
        if (n.event) {
          d.buzz?.onNotice(n.event);
          d.tabIcon?.onNotice(n.event);
        }
      },

      onChat: (line) => {
        d.state.addChat(line);
        // The backlog a join replays is not news, so it arrives silently.
        // A quick line of a squad mate, a rally among them, is the squad
        // signal in place of the chat blip (R3135).
        const squad = line.channel === ChatChannel.CHAT_CHANNEL_SQUAD;
        if (squadPingLine(line, squad, d.state.selfAccountId, d.scene.squadIds)) d.bus.local(EV_SQUAD_PING);
        else if (!line.history) d.bus.local('chat_line');
        // A quick line with a position rings that point once (R1607,
        // card 04). A backlog line rings nothing: it is not news.
        if (!line.history && line.quick > 0 && line.quickPos) d.state.addQuickPing(line.quickPos.cell);
        // A mate's RALLY buzzes a telephone: the H-hour line on SQUAD.
        d.buzz?.onChat(line, squad, d.state.selfAccountId);
      },

      onClan: (info) => {
        d.state.setClan(info);
        // The squad, not the clan (R199): a mark that goes to a whole
        // clan marks nothing, and squad 0 is "unassigned". Both map
        // layers mark a squadmate's things — a hollow hull, a plate with no
        // rim (R185) — and the panels name one, so all three read the one
        // set the scene holds.
        //
        // The slot map is the client's own arithmetic (R830, §6.1): the
        // wire carries a SQUAD and never a place inside it, so the slot is
        // the rank of the account id among that squad's members. The
        // `slot-numeral` value of a Marks body prints it.
        d.scene.setSquadIds(info.squad === 0
          ? new Set<number>()
          : new Set(info.members.filter((m) => m.squad === info.squad)
            .map((m) => m.accountId)),
        squadSlotsOf(info.members, info.squad));
        this.onSquad(info);
      },

      onMarket: (m) => { d.state.setMarket(m); },

      // The casino's three frames (casino.md). Each one files what the
      // wire said and nothing else: the panel draws from `state.casino`
      // on the chrome's own 4 Hz pass, and the table frame's arrival
      // stamp is taken inside `setCasinoTable`, so the countdown anchors
      // on the moment the frame landed.
      onCasinoLobby: (l) => {
        d.state.setCasinoLobby(l);
        // A lobby that seats the reader nowhere drops a held verdict cue.
        resultCue.onTable(d.state.casino.table?.id ?? 0);
      },
      onCasinoTable: (t) => {
        const was = d.state.casino.table;
        d.state.setCasinoTable(t);
        resultCue.onTable(t.id);
        // The seat, the deal, the chip, the turn and the wheel are edges
        // between the frame that was and the frame that came, which is the
        // rule of `audio/cues.ts` (`ui/casino/resultcue.ts` `tableCues`).
        for (const id of tableCues(was, t)) d.audio?.play(id);
      },
      onCasinoResult: (r) => {
        d.state.setCasinoResult(r);
        // The cues of a result wait for the picture: the wheel settles a
        // spin in the tick it starts to turn, and the text waits for the
        // ball. The ball, each reel stop and the verdict sound when the
        // picture shows them (`ui/casino/resultcue.ts`). A dissolved table
        // sounds nothing.
        resultCue.onResult(r, reducedMotion());
      },

      onSieges: (list) => {
        d.state.setSieges(list);
        d.scene.setSieges(list);
        // A siege opening is the server's `siege_defend` and `siege_attack`
        // events (R1147). This client
        // keeps no copy of the contested set to find the edge with.
      },

      onScoreboard: (b) => { d.state.setBoard(b); },
      onSeasonReport: (r) => { d.state.setSeasonReport(r); },
      onSeasonSummary: (s) => { d.state.setSeasonSummary(s); d.onSeasonSummary?.(); },
      // The return briefing (R1605): the store holds it, and the dock opens
      // on the INBOX, where the block heads the list (card 04).
      onReturnBriefing: (b) => { d.state.setBriefing(b); d.ui().openBriefing(); },
      onWar: (w) => { d.state.setWar(w); },
      onProduction: (p) => { d.state.setProduction(p); },
      onBuildingPreview: (p) => { d.state.setBuildingPreview(p); },
      onTimelapse: (t) => { d.state.setTimelapse(decodeTimelapse(t.data)); },
      // The inbox ledger (R1851): the store holds it, and SINCE YOU LEFT
      // and the DIGEST sum it at the next render.
      onInboxLedger: (l) => { d.state.setInboxLedger(l); },
      onDevBuild: (dev) => { d.state.setDev(dev); },
      // The wrecks of the chart furniture (R801, card 08). The controller
      // sees the list grow by one compare a frame, so nothing is marked here.
      onWrecks: (list) => { d.state.addWrecks(list); },
      // The map markers of the chart furniture (R1292). The frame carries
      // the whole list, and the controller finds the new list by one
      // identity compare a frame, so nothing is marked here. A marker a
      // squad mate placed is the squad signal (R3135).
      onMarkers: (list) => {
        d.state.setMapMarkers(list);
        d.cues?.onMarkers(list, d.state.selfAccountId, d.scene.squadIds);
      },
      onInbox: (msgs) => {
        const before = inboxMark(d.state.inbox);
        d.state.setInbox(msgs);
        // The battle marks of the chart furniture are the last day's fights
        // out of this list (R548, card 52).
        d.scene.chart?.invalidate('inbox');
        // SINCE YOU LEFT states the sums of the inbox ledger (R1851). A list
        // that changed while the INBOX stands open asks for them again, so
        // the line never reads "nothing new" over a new loss. The world
        // answers one ask a second, which absorbs a burst.
        if (inboxMark(msgs) !== before && d.ui().inboxShown) d.conn().sendInboxLedgerQuery();
      },
      // One reached grade (achievements.md §4). The queue decides whether
      // the card stands now, waits behind another, or collapses into a
      // burst; nothing about that is decided here.
      onAchievementUnlock: (u) => {
        d.unlocks?.push({
          code: u.code, grade: u.grade, value: Number(u.value), kind: u.kind,
        });
      },

      // The epicentre of the chart furniture is the next seaquake shock
      // (R803, card 54): every frame moves the mark or its countdown.
      onQuake: (q) => {
        d.state.setQuake(q);
        d.scene.chart?.invalidate('quake');
      },

      // The settlement clock (R2093): the drain banner and the bar's
      // settlement claim read it.
      onSettlement: (c) => { d.state.setSettlement(c); },

      // The answer to `openReplay`: one recorded engagement, served whole.
      // The viewer reads it and simulates nothing.
      onReplay: (r) => { d.openReplay(r); },

      onHeatmap: (h) => {
        // Straight from the socket to the layer: 20 KB of bytes is not
        // state the DOM ever reads.
        d.heatmap.setFrame(h.kind, h.cells);
      },

      // The maintenance window the meta server plans (deployment.md §6.1).
      // It is a store and nothing else: the drain line reads it every
      // frame, and the cover is raised by the close code and not by this.
      onMaintenanceInfo: (m) => {
        d.state.setMaintenance(m.startsAtUnix, m.endsAtUnix, m.note);
      },

      onFatal: (reason, kind) => { d.onFatal(reason, kind); },
    };
  }

  // -- the inbox refetch ---------------------------------------------------

  /**
   * Ask for the inbox again when the unread badge rises (R694).
   *
   * The hello asks once, and the INBOX tab asks when it opens. Without this
   * arm a sinking after the join put no battle mark on the map until the
   * player opened the panel. `Player.InboxUnread` counts every unread kind,
   * so a kill raises the badge as well as a loss.
   *
   * A rise the floor holds back stays pending, or the last sinking of a
   * burst never reaches the map. Empire frames arrive continuously, so the
   * held fetch goes out on the first frame past the gap.
   */
  private pollInbox(unread: number): void {
    if (unread > this.lastUnread) this.pendingInboxFetch = true;
    this.lastUnread = unread;
    if (!this.pendingInboxFetch) return;
    const now = Date.now();
    if (now - this.lastInboxFetchMs < CHROME_INBOX_REFETCH_MS) return;
    this.pendingInboxFetch = false;
    this.lastInboxFetchMs = now;
    this.deps.conn().sendInbox({ fetch: true });
  }

  // -- the hello -----------------------------------------------------------

  private onHello(h: Hello): void {
    const d = this.deps;
    if (h.protocol !== PROTOCOL) {
      // An old server ignores `?proto=` and lets us in anyway — the hello is
      // the second interlock. Stop for good rather than play garbled.
      d.conn().fatal(S.protocol_mismatch({ server: h.protocol, client: PROTOCOL }));
      return;
    }
    // Which arrival this is, read from the hello that is still stored: no
    // hello yet is a login, a new season number is a season roll, and a
    // re-hello inside the same season is a resync (a seaquake stage-1
    // rebuild, an admin reseed), which plays a short fade rather than a full
    // arrival.
    // A changed world id counts as a season roll too (R766): a handover
    // can land this client in another world at the same season number, and
    // a resync's short fade is the wrong answer to a map that is new from
    // the sea floor up.
    const prev = d.state.hello;
    const kind: SpawnKind = prev === null ? 'login'
      : prev.season !== h.season || prev.worldId !== h.worldId ? 'season' : 'resync';
    // The black goes up before the scene comes down, so the chrome snaps
    // outside the viewport unseen — a player must not watch the panels leave
    // (R182). The caller covers on a season roll alone.
    d.arrival?.cover(kind);
    // A login and a season roll take the next clan frame as the squad's
    // baseline, so neither says SQUAD CHANGED (R1571).
    this.squadWatch.hello(kind);
    // The first marker list of this hello is the state of the world.
    d.cues?.onHello();

    // The map, in one call (R503). It applies the grid FIRST, so no layer
    // reads a geometry the hello has already replaced.
    d.scene.applyHello(h);

    d.state.setHello(h);
    // The chrome takes this world's banner (R243).
    wearTheme(h.faction);
    // The event table, straight from the server. A reconnect replaces it
    // whole, because an operator can have changed the rows between joins.
    d.bus.setCatalog(h.catalog?.events ?? [], h.catalog?.eventDefaults);
    const ui = d.ui();
    ui.setNoticeDedup(d.bus.defaultDedupMs);
    ui.setNoticeMs(d.bus.defaultToastMs);

    // The heat map reads the mask the terrain layer now holds.
    const blocked = d.scene.blocked;
    d.heatmap.setBlocked(blocked);

    // A heatmap subscription belongs to the socket, so a reconnect starts
    // the server side at NONE. Ask again for whatever the player had on,
    // unless the client draws the mode itself (R2870).
    const serverHeat = heatServerKind(d.state.heatmap);
    if (serverHeat !== HeatmapKind.HEATMAP_KIND_NONE) {
      d.conn().sendHeatmapMode(serverHeat);
    }
    // The advisor switch is the empire's (R1566): the join ticket states
    // it and the Empire frame carries it back, so the hello sends nothing.

    // The inbox, once, at the join (R548, card 52). The battle marks stand
    // on the map from the first frame, so a reader reaches the film of a
    // fight from the water it was fought in without opening the INBOX panel
    // first. The answer arrives on `onInbox`, which stales the chart.
    // The stamp holds the refetch floor down (R694), so the first empire
    // frame after the join does not ask a second time.
    d.conn().sendInbox({ fetch: true });
    this.lastInboxFetchMs = Date.now();

    // The chart takes this world's rectangle and its owner bytes (R261).
    d.minimap.rescale();
    d.minimap.setOwners(d.scene.owners);
    // The two hex views take the same array the chart does. They hold the
    // reference: `applyFlip` writes those bytes in place, and a flip only
    // stales the picture (R456, R501).
    d.hexFills.setOwners(d.scene.owners, blocked);
    d.hexBorders.setOwners(d.scene.owners, blocked);

    d.reportViewport();
    ui.render(true);

    // The map arrives (R163). The camera is already where the arrival wants
    // it, and the scene under the veil is live.
    d.arrival?.start(kind);
  }

  /**
   * The squad on the map (R1571, mockup round 075 cards 18 and 19). A
   * clan frame that moved the reader to another squad raises one notice,
   * pulses the chip and lifts the new squad's hulls, which the scene has
   * just been handed; reduced motion draws no lift. The first frame of a
   * login or a season is the baseline and silent, except that a pickup
   * squad says what it is one time a season.
   */
  private onSquad(info: ClanInfo): void {
    const d = this.deps;
    const ev = this.squadWatch.clan(info);
    if (ev === null) return;
    if (ev.kind === 'changed') {
      const ui = d.ui();
      ui.notice(changeNotice(ev.view), { key: 'squad' });
      ui.flashSquad();
      if (ev.view !== null && !reducedMotion()) d.scene.liftSquad();
      return;
    }
    const h = d.state.hello;
    if (h && pickupNoticeDue(ev.view, h.worldId, h.season, d.state.selfAccountId)) {
      d.ui().notice(pickupNotice(ev.view!), { key: 'squad' });
    }
  }

  // -- the state frame -----------------------------------------------------

  private onState(tick: number, s: State): void {
    const d = this.deps;
    this.lastTick = tick;

    // An own hull the wire drops has come home, or it sank (R848). A
    // commander hears every own hull, so the drop is never a lost contact,
    // and a fleet window that read "contact lost" over a hull in the
    // harbour named the wrong thing. The window closes in the frame of the
    // drop, through the one door, the way a removed site takes its panel
    // away (R204). A foreign contact keeps the panel: its drop IS a lost
    // contact, and the words are right. The ownership is read before the
    // scene lets the record go. A full frame names no drops, so there the
    // absence from the picture is the drop.
    // A hull that a pirate group took is gone for good as well (ruling R-g,
    // R2721): the server drops its id and sends a new hull of the NPC side,
    // so its window closes in the frame of the drop, whoever reads it.
    const sel = d.state.selection;
    const homing = sel?.kind === 'fleet'
      && (s.full ? !s.fleets.some((f) => f.id === sel.id) : s.removed.includes(sel.id))
      && (d.scene.fleets.describe(sel.id)?.own === true || d.scene.fleets.isBeingTaken(sel.id));

    // The time a scan reveal still holds, for the SCANNED chip and the
    // fade of a contact's card (round 084 card 05, R1837).
    d.state.scanSeen.observe(s.fleets, s.removed, s.full, Date.now(), d.state.tacticalScale);
    d.scene.applyState(s);
    if (homing) d.commands().select(null);
    // The frame's own edges (R547): a reload that finished, a crew that
    // tired, a hull a hostile now hears, a scan, a cable on the floor, a
    // sortie and a round running at one of this commander's hulls.
    d.cues?.onState(s, d.state.faction);
    // No sweep of the control groups here: the server is the store, and a
    // fleet that comes home keeps its slot (R540). A slot that names a hull
    // this frame dropped draws no badge and answers no digit until an
    // Empire frame settles it, which a sinking sends and a docking does not.

    // A beam change stales the WAR data the way a flip does — the overlay
    // re-queries only while the tab is on screen, debounced (R83). The
    // owners and every flip raised it from the scene's hooks already. The
    // rows are deltas (R2442), so a frame that moved no beam raises nothing.
    if (d.scene.beamsChanged) d.ui().warEvent();
    // The first finds of the faction (v2 §4.3, round 088 card 06, R2626):
    // one line each in the notice lane, a GO TO to the point, and no buzz,
    // because a find is a line and no alarm.
    for (const n of findNotices(d.scene.finds)) d.ui().notice(n.text, n.opts);
    // Fresh data under a stationary cursor: re-render the tooltip in place
    // — the overlay only touches the DOM when the markup changed.
    d.tooltip().refreshAtCursor();
  }
}

/**
 * The notice line of each first find (R2626): FIRST FIND and the title of
 * the point, keyed by the point, with its cell for the GO TO. Only the
 * kinds of phase 3 write a line (`poiShown`): a seeded cache has a title
 * for its peek (R2735), and still no notice.
 */
export function findNotices(finds: readonly PointOfInterest[]): { text: string; opts: NoticeOptions }[] {
  const out: { text: string; opts: NoticeOptions }[] = [];
  for (const p of finds) {
    const kind = poiShown(p.kind) ? poiTitle(p.kind) : '';
    if (kind === '') continue;
    out.push({ text: S.first_find({ kind }), opts: { key: `find:${p.id}`, cell: p.cell } });
  }
  return out;
}
